'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { PromoBanner } from 'ui/components/PromoBanner/PromoBanner';
import { useTranslation } from '@whoop/i18n/lang/client';
import { BANNER_THEMES } from 'ui/types/PromoCodeThemes';
import { formatDate } from '@whoop/i18n';
import { ANNUAL_MEMBERSHIP_KEY, TWENTYFOUR_MONTH_MEMBERSHIP_KEY } from 'ui/utils/constants/membershipConstants';
import { getFormatters } from 'ui/hooks/formatter';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { useGetCurrentPageMaverick } from '@/hooks/useGetCurrentPageMaverick';
import { getUTCDateWithoutOffset } from '@/lib/utils/dateHelpers';
import { getBannerTextByFlowType, replaceTemplateMembershipPrices } from '@/lib/utils/promoBannerHelpers';
import { isJFStepsPage, isUpgradeStepsPage } from '@/lib/utils/maverickHelpers';
import { CONTENTFUL_UPGRADE_FLOW_TYPE, CONTENTFUL_JOIN_FLOW_TYPE } from '@/lib/constants/maverick';
export const PromoBannerWrapper = (): JSX.Element | null => {
  const page = useGetCurrentPageMaverick();
  const {
    siteWidePromoContent,
    language,
    region,
    memberships,
    currency,
    regionalBanners
  } = useSiteStore(useShallow(state => ({
    siteWidePromoContent: state.siteWidePromoContent,
    language: state.language,
    region: state.region,
    memberships: state.ecommCatalog.memberships,
    currency: state.currency,
    regionalBanners: state.regionalBanners
  })));
  const {
    formatDisplayPrice
  } = getFormatters({
    currency,
    language,
    region
  });
  const {
    t
  } = useTranslation('joinLandingPage', language);
  const [siteBannerText, setSiteBannerText] = useState<string | undefined>(undefined);
  const isJoinFlowIncludedPage = isJFStepsPage(page);
  const isUpgradeFlowIncludedPage = isUpgradeStepsPage(page);
  const flowType = isJoinFlowIncludedPage ? CONTENTFUL_JOIN_FLOW_TYPE : CONTENTFUL_UPGRADE_FLOW_TYPE;
  const displayBanner = isJoinFlowIncludedPage || isUpgradeFlowIncludedPage;
  useEffect(() => {
    if (!siteBannerText) {
      const bannerText = getBannerTextByFlowType({
        regionalBanners,
        flowType
      });
      setSiteBannerText(bannerText);
    }
  }, [regionalBanners]);

  // TODO : change this for mav JF - as follow up since no promo right now
  // https://whoopinc.atlassian.net/browse/ECOMM-2163
  const annualDiscountedPrice = useMemo(() => {
    return memberships.find(membership => membership.key === ANNUAL_MEMBERSHIP_KEY)?.variants.find(variant => variant.price?.discounted)?.price?.discounted;
  }, [memberships]);
  const twentyfourMonthDiscountedPrice = useMemo(() => {
    return memberships.find(membership => membership.key === TWENTYFOUR_MONTH_MEMBERSHIP_KEY)?.variants.find(variant => variant.price?.discounted)?.price?.discounted;
  }, [memberships]);
  const promoEndDate = siteWidePromoContent?.promoEndDate ? new Date(siteWidePromoContent.promoEndDate) : undefined;
  const absolutePromoEndDate = promoEndDate ? getUTCDateWithoutOffset(promoEndDate) : undefined;
  const isTodayAfterDate = absolutePromoEndDate ? new Date() > absolutePromoEndDate : false;
  const validUntil = absolutePromoEndDate && !isTodayAfterDate ? t('ends', {
    date: formatDate(absolutePromoEndDate, language, {
      month: 'short',
      day: 'numeric'
    })
  }) : undefined;

  // If sitewide banner content, driven by contentful
  if (siteBannerText) {
    return displayBanner ? <PromoBanner content={siteBannerText} /> : null;
  }

  // todo: we currently don't have any of the below banners in old or new flow displaying
  // however will need to update this post-launch, since new flow does not use ecommCatalog

  // For partnerships, raf gift, and anything with a promo code that drives a banner
  if (!siteWidePromoContent?.bannerText) {
    return null;
  }
  const bannerText = replaceTemplateMembershipPrices({
    template: siteWidePromoContent?.bannerText ?? '',
    annualPrice: annualDiscountedPrice ? formatDisplayPrice(annualDiscountedPrice.value.cent_amount) : undefined,
    twentyfourMonthPrice: twentyfourMonthDiscountedPrice ? formatDisplayPrice(twentyfourMonthDiscountedPrice.value.cent_amount) : undefined
  });
  return bannerText ? <PromoBanner content={bannerText} tagText={validUntil} theme={BANNER_THEMES[siteWidePromoContent.theme ?? 'default']} data-sentry-element="PromoBanner" data-sentry-component="PromoBannerWrapper" data-sentry-source-file="PromoBannerWrapper.tsx" /> : null;
};